import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Image as ImageIcon, Check, X } from "lucide-react";
import { toast } from "sonner";

// Curated, vivid sports imagery (Unsplash) — ESPN-style energy (action shots, saturated)
export const SPORTS_IMAGE_PRESETS = [
    // Football/Soccer action
    "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1200&q=80", // stadium lights
    "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=1200&q=80", // football close
    "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1200&q=80", // kick action
    "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=1200&q=80", // ball on grass
    "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=1200&q=80", // stadium crowd
    "https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=1200&q=80", // night match
    // Premier League / EPL energy
    "https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=1200&q=80", // player run
    "https://images.unsplash.com/photo-1577471488278-16eec37ffcc2?w=1200&q=80", // celebration
    "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=1200&q=80", // goalkeeper dive
    // Basketball
    "https://images.unsplash.com/photo-1504450758481-7338eba7524a?w=1200&q=80", // nba dunk
    "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1200&q=80", // basket court
    // African football
    "https://images.unsplash.com/photo-1542820229-081e0c12af0b?w=1200&q=80",
    "https://images.unsplash.com/photo-1626197031507-c17099753214?w=1200&q=80",
    // Boxing
    "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=1200&q=80",
    // Athletics / track
    "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1200&q=80",
    // Tennis
    "https://images.unsplash.com/photo-1530915534160-f3be7beffb4e?w=1200&q=80",
    // F1 racing
    "https://images.unsplash.com/photo-1568595293370-152e7c0fa19f?w=1200&q=80",
    // Generic energy
    "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1200&q=80",
];

const EditImageDialog = ({ open, onOpenChange, contentId, currentUrl, onSaved }) => {
    const { token } = useAuth();
    const [url, setUrl] = useState(currentUrl || "");
    const [preview, setPreview] = useState(currentUrl || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setUrl(currentUrl || "");
        setPreview(currentUrl || "");
    }, [currentUrl, open]);

    const handlePreview = () => {
        setPreview(url);
    };

    const pickPreset = (u) => {
        setUrl(u);
        setPreview(u);
    };

    const save = async () => {
        if (!url) {
            toast.error("Weka URL ya picha");
            return;
        }
        setSaving(true);
        try {
            await axios.put(
                `${API}/content/${contentId}`,
                { image_url: url },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Picha imebadilishwa");
            onSaved?.(url);
            onOpenChange(false);
        } catch {
            toast.error("Imeshindikana");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-paper border-border text-white max-w-3xl" data-testid="edit-image-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-electric" />
                        Badilisha Picha ya Makala
                    </DialogTitle>
                </DialogHeader>

                {/* Live preview */}
                <div className="aspect-[16/9] bg-subtle rounded overflow-hidden border border-border">
                    {preview ? (
                        <img
                            src={preview}
                            alt="preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.currentTarget.src =
                                    "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800";
                            }}
                            data-testid="edit-image-preview"
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center text-white/30 text-sm">
                            Weka URL ya picha hapa chini
                        </div>
                    )}
                </div>

                {/* URL input */}
                <div className="space-y-2">
                    <label className="text-white/60 text-xs uppercase tracking-wider">URL ya Picha</label>
                    <div className="flex gap-2">
                        <Input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://..."
                            className="bg-subtle border-border text-white"
                            data-testid="edit-image-url-input"
                            onKeyDown={(e) => e.key === "Enter" && handlePreview()}
                        />
                        <Button
                            onClick={handlePreview}
                            variant="outline"
                            className="border-border text-white/70"
                            data-testid="edit-image-preview-btn"
                        >
                            Onyesha
                        </Button>
                    </div>
                </div>

                {/* Quick presets */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-white/60 text-xs uppercase tracking-wider">Chagua Picha Bora</label>
                        <Badge className="bg-electric/10 text-electric border-0 text-[10px]">
                            {SPORTS_IMAGE_PRESETS.length} stock
                        </Badge>
                    </div>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                        {SPORTS_IMAGE_PRESETS.map((u, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => pickPreset(u)}
                                className={`relative aspect-[4/3] rounded overflow-hidden border-2 transition-all ${
                                    url === u ? "border-electric ring-2 ring-electric/30" : "border-border hover:border-electric/40"
                                }`}
                                data-testid={`preset-${i}`}
                            >
                                <img src={u.replace("w=1200", "w=200")} alt="" className="w-full h-full object-cover" />
                                {url === u && (
                                    <div className="absolute inset-0 bg-electric/20 flex items-center justify-center">
                                        <Check className="h-6 w-6 text-white" />
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="edit-image-cancel">
                        <X className="h-4 w-4 mr-1" />
                        Ghairi
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || !url}
                        className="bg-electric hover:bg-vodaredDark text-white"
                        data-testid="edit-image-save"
                    >
                        <Check className="h-4 w-4 mr-1" />
                        {saving ? "Inahifadhi..." : "Hifadhi Picha"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditImageDialog;
